/**
 * Bulk inquiry builder.
 *
 * The visitor taps chips, colours and steppers (there is no text box) and the yellow button opens WhatsApp with a
 * complete message already written. Every word comes from the section settings (data-config); nothing is saved or sent
 * to any server. The chosen choices are shown in the summary and written into the message.
 */

const MIN_SPOOLS = 1;
const MAX_SPOOLS = 500;
const DEFAULT_SPOOLS = 5;

class SureSpoolBulk extends HTMLElement {
  /** @type {AbortController | null} */
  abort = null;
  /** @type {Record<string, string>} */
  cfg = {};
  /** The wording for the message, and the settings texts, come from the section settings. */

  connectedCallback() {
    this.abort = new AbortController();
    const { signal } = this.abort;
    try {
      this.cfg = JSON.parse(this.dataset.config || '{}');
    } catch (error) {
      this.cfg = {};
    }

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('input', this.#onInput, { signal });
    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('keydown', this.#onKeydown, { signal });
    this.#update();
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  /** @returns {HTMLElement[]} */
  get #tiles() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-bulk-tile]')]);
  }

  #qty(/** @type {HTMLElement} */ tile) {
    const input = tile.querySelector('[data-bulk-qty]');
    const value = input instanceof HTMLInputElement ? parseInt(input.value, 10) : 0;
    return Number.isFinite(value) ? value : 0;
  }

  #setQty(/** @type {HTMLElement} */ tile, /** @type {number} */ value) {
    const input = tile.querySelector('[data-bulk-qty]');
    if (!(input instanceof HTMLInputElement)) return;
    const clamped = Math.max(MIN_SPOOLS, Math.min(MAX_SPOOLS, Math.floor(Number(value) || MIN_SPOOLS)));
    if (input.value !== String(clamped)) input.value = String(clamped);
  }

  #isSelected(/** @type {HTMLElement} */ tile) {
    return tile.querySelector('[data-bulk-toggle]')?.getAttribute('aria-pressed') === 'true';
  }

  #onClick = (/** @type {Event} */ event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const chip = target.closest('[data-bulk-chip]');
    if (chip instanceof HTMLElement) {
      const group = chip.dataset.bulkChip;
      const wasOn = chip.getAttribute('aria-pressed') === 'true';
      // One choice per group; tapping the chosen one again clears it.
      this.querySelectorAll(`[data-bulk-chip="${group}"]`).forEach((other) => other.setAttribute('aria-pressed', 'false'));
      chip.setAttribute('aria-pressed', wasOn ? 'false' : 'true');
      this.#update();
      return;
    }

    const toggle = target.closest('[data-bulk-toggle]');
    if (toggle) {
      const tile = /** @type {HTMLElement | null} */ (toggle.closest('[data-bulk-tile]'));
      if (!tile) return;
      const on = toggle.getAttribute('aria-pressed') !== 'true';
      toggle.setAttribute('aria-pressed', on ? 'true' : 'false');
      const stepper = tile.querySelector('.ss-bulk__stepper');
      if (stepper instanceof HTMLElement) stepper.hidden = !on;
      this.#setQty(tile, DEFAULT_SPOOLS);
      this.#update();
      return;
    }

    const plus = target.closest('[data-bulk-plus]');
    const minus = target.closest('[data-bulk-minus]');
    if (plus || minus) {
      const tile = /** @type {HTMLElement | null} */ ((plus || minus)?.closest('[data-bulk-tile]') ?? null);
      if (!tile) return;
      this.#setQty(tile, this.#qty(tile) + (plus ? 1 : -1));
      this.#update();
      return;
    }

    if (target.closest('[data-bulk-sample]')) {
      const button = /** @type {HTMLElement} */ (target.closest('[data-bulk-sample]'));
      button.setAttribute('aria-pressed', button.getAttribute('aria-pressed') === 'true' ? 'false' : 'true');
      this.#update();
      return;
    }

    if (target.closest('[data-bulk-pincode]')) {
      const button = /** @type {HTMLElement} */ (target.closest('[data-bulk-pincode]'));
      button.setAttribute('aria-pressed', button.getAttribute('aria-pressed') === 'true' ? 'false' : 'true');
      this.#update();
      return;
    }

    if (target.closest('[data-bulk-send]')) {
      this.#send();
      return;
    }

    if (target.closest('[data-bulk-copy]')) {
      this.#copy();
    }
  };

  #onInput = (/** @type {Event} */ event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.matches('[data-bulk-qty]')) return;
    const tile = /** @type {HTMLElement | null} */ (target.closest('[data-bulk-tile]'));
    if (!tile) return;
    // While typing an empty box is left alone; any number is kept inside 1 to 500.
    if (target.value !== '') this.#setQty(tile, parseInt(target.value, 10));
    this.#update();
  };

  #onChange = (/** @type {Event} */ event) => {
    const target = event.target;
    if (target instanceof HTMLInputElement && target.matches('[data-bulk-qty]')) {
      const tile = /** @type {HTMLElement | null} */ (target.closest('[data-bulk-tile]'));
      if (tile) this.#setQty(tile, this.#qty(tile));
    }
    this.#update();
  };

  #onKeydown = (/** @type {KeyboardEvent} */ event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.matches('[data-bulk-qty]')) return;
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
    event.preventDefault();
    const tile = /** @type {HTMLElement | null} */ (target.closest('[data-bulk-tile]'));
    if (!tile) return;
    this.#setQty(tile, this.#qty(tile) + (event.key === 'ArrowUp' ? 1 : -1));
    this.#update();
  };

  /** What the visitor chose, read from the page. */
  #state() {
    const chosen = (/** @type {string} */ group) => {
      const chip = this.querySelector(`[data-bulk-chip="${group}"][aria-pressed="true"]`);
      return chip instanceof HTMLElement ? { label: chip.dataset.value || '', message: chip.dataset.message || chip.dataset.value || '', note: chip.dataset.note || '' } : null;
    };
    const colours = this.#tiles
      .filter((tile) => this.#isSelected(tile))
      .map((tile) => ({
        name: tile.dataset.name || '',
        kind: tile.dataset.kind || 'std',
        oos: tile.dataset.oos === 'true',
        qty: Math.max(MIN_SPOOLS, Math.min(MAX_SPOOLS, this.#qty(tile) || MIN_SPOOLS)),
      }));
    const stateSelect = this.querySelector('[data-bulk-state]');
    return {
      printers: chosen('printers'),
      material: chosen('material'),
      frequency: chosen('frequency'),
      timing: chosen('timing'),
      colours,
      total: colours.reduce((sum, colour) => sum + colour.qty, 0),
      sample: this.querySelector('[data-bulk-sample]')?.getAttribute('aria-pressed') === 'true',
      pincode: this.querySelector('[data-bulk-pincode]')?.getAttribute('aria-pressed') === 'true',
      deliveryState: stateSelect instanceof HTMLSelectElement ? stateSelect.value : '',
    };
  }

  #unit(/** @type {number} */ count) {
    return count === 1 ? this.cfg.unitOne || 'spool' : this.cfg.unitMany || 'spools';
  }

  #totalText(/** @type {number} */ total) {
    return (this.cfg.totalText || 'Total: [count] [unit] ([kg] kg)')
      .replace('[count]', String(total))
      .replace('[unit]', this.#unit(total))
      .replace('[kg]', String(total));
  }

  /** The message sent to WhatsApp, in plain lines. */
  #message() {
    const s = this.#state();
    const c = this.cfg;
    const lines = [c.intro];
    if (s.printers) lines.push(`${c.printers} ${s.printers.message}`);
    if (s.material) lines.push(`${c.material} ${s.material.message}`);
    lines.push(c.colours);
    s.colours.forEach((colour) => {
      const tail = colour.kind === 'extra' ? ` (${c.request})` : '';
      const flag = colour.kind === 'std' && colour.oos ? ` (${c.oos})` : '';
      lines.push(`- ${colour.name}${tail}: ${colour.qty} ${this.#unit(colour.qty)}${flag}`);
    });
    lines.push(`${c.total} ${s.total} ${this.#unit(s.total)} (${s.total} kg)`);
    if (s.frequency) lines.push(`${c.frequency} ${s.frequency.message}`);
    if (s.timing) lines.push(`${c.timing} ${s.timing.message}`);
    if (s.deliveryState) lines.push(`${c.state} ${s.deliveryState}`);
    if (s.pincode) lines.push(sentence(c.pincode));
    if (s.sample) lines.push(sentence(c.sample));
    return lines.join('\n');
  }

  /** The plain-words list in the summary card. */
  #summaryLines() {
    const s = this.#state();
    const c = this.cfg;
    /** @type {string[]} */
    const lines = [];
    if (s.printers) lines.push(`${c.printers} <b>${esc(s.printers.label)}</b>`);
    if (s.material) lines.push(`${c.material} <b>${esc(s.material.label)}</b>`);
    s.colours.forEach((colour) => {
      const tail = colour.kind === 'extra' ? ` (${esc(c.otherLabel || '')})` : '';
      const flag = colour.kind === 'std' && colour.oos ? ` (${esc(c.oos)})` : '';
      lines.push(`<b>${esc(colour.name)}</b>${tail}: ${colour.qty} ${this.#unit(colour.qty)}${flag}`);
    });
    if (s.frequency) lines.push(`${c.frequency} <b>${esc(s.frequency.label)}</b>`);
    if (s.timing) lines.push(`${c.timing} <b>${esc(s.timing.label)}</b>`);
    if (s.deliveryState) lines.push(`${c.state} <b>${esc(s.deliveryState)}</b>`);
    if (s.pincode) lines.push(esc(c.pincodeLine || c.pincode));
    if (s.sample) lines.push(esc(c.sampleLine || c.sample));
    return lines;
  }

  #update() {
    const s = this.#state();
    const ready = s.total > 0;

    // The "Other material" note (no typing: a line only).
    const note = this.querySelector('[data-bulk-material-note]');
    if (note instanceof HTMLElement) {
      const text = s.material?.note || '';
      note.hidden = !text;
      if (note.textContent !== text) note.textContent = text;
    }

    this.#text('[data-bulk-total]', this.#totalText(s.total));
    this.#text('[data-bulk-sum-total]', ready ? this.#totalText(s.total) : '');

    const list = this.querySelector('[data-bulk-lines]');
    if (list) {
      const lines = this.#summaryLines();
      const html = lines.length ? lines.map((line) => `<li>${line}</li>`).join('') : `<li class="is-empty">${esc(this.cfg.emptySummary || '')}</li>`;
      if (list.innerHTML !== html) list.innerHTML = html;
    }

    this.querySelectorAll('[data-bulk-send]').forEach((button) => {
      if (button instanceof HTMLButtonElement) button.disabled = !ready;
    });
    const copy = this.querySelector('[data-bulk-copy]');
    if (copy instanceof HTMLButtonElement) copy.disabled = !ready;
    this.#text('[data-bulk-hint]', ready ? '' : this.cfg.hint || '');
    this.#text('[data-bulk-bar-text]', ready ? this.#totalText(s.total) : this.cfg.hint || '');
    if (!ready) this.#text('[data-bulk-done]', '');
  }

  #text(/** @type {string} */ selector, /** @type {string} */ text) {
    this.querySelectorAll(selector).forEach((element) => {
      if (element.textContent !== text) element.textContent = text;
    });
  }

  /** Opens WhatsApp with the message already written (only the text part is encoded). */
  #send() {
    if (this.#state().total <= 0) return;
    const url = `https://wa.me/${this.cfg.number}?text=${encodeURIComponent(this.#message())}`;
    const opened = window.open(url, '_blank', 'noopener');
    if (!opened) window.location.href = url;
    this.#text('[data-bulk-done]', this.cfg.done || '');
  }

  async #copy() {
    if (this.#state().total <= 0) return;
    const text = this.#message();
    try {
      await navigator.clipboard.writeText(text);
      this.#text('[data-bulk-done]', this.cfg.copied || '');
    } catch (error) {
      // Older browsers: copy through a temporary box.
      const box = document.createElement('textarea');
      box.value = text;
      box.setAttribute('readonly', '');
      box.style.cssText = 'position:fixed;inset-block-start:0;opacity:0;pointer-events:none;';
      document.body.appendChild(box);
      box.select();
      let ok = false;
      try {
        ok = document.execCommand('copy');
      } catch (inner) {
        ok = false;
      }
      box.remove();
      this.#text('[data-bulk-done]', ok ? this.cfg.copied || '' : this.cfg.copyFailed || '');
    }
  }
}

/** A message line ends with a full stop. */
function sentence(/** @type {string} */ text) {
  return /[.!?]$/.test(text) ? text : `${text}.`;
}

function esc(/** @type {string} */ value) {
  return String(value ?? '').replace(/[&<>"]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[ch] || ch);
}

if (!customElements.get('surespool-bulk')) {
  customElements.define('surespool-bulk', SureSpoolBulk);
}
